import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, BarChart3, FileText } from 'lucide-react';
import { KpiGrid, SectionHeader, ExportButtons, ReportTable, ReportEmpty, fIDR, fIDRCompact } from './shared';

const CHART_AXIS = '#94A3B8';
const CHART_GRID = 'rgba(148,163,184,0.14)';
const TT_STYLE = { background: '#0B1020', border: '1px solid #334155', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#EAF0FF' };
const TT_LABEL = { color: '#CBD5E1', fontWeight: 600, fontSize: 11 };
const TT_ITEM = { color: '#EAF0FF', fontSize: 12 };
const PIE_COLORS = ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export function SummaryTab({ data, loading, filters }) {
  const s = data?.summary || {};
  const cards = [
    { label: 'Total Invoice', value: fIDRCompact(s.total_invoice), sub: `${s.total_invoice_count || 0} invoice`, accent: 'primary' },
    { label: 'Total DPP', value: fIDRCompact(s.total_dpp), accent: '' },
    { label: 'DPP Nilai Lain', value: fIDRCompact(s.total_dpp_nilai_lain), accent: '' },
    { label: 'Total PPN', value: fIDRCompact(s.total_ppn), accent: 'primary' },
    { label: 'Total PPh Dipotong', value: fIDRCompact(s.total_pph), accent: 'warn' },
    { label: 'Total Pembayaran', value: fIDRCompact(s.total_paid), accent: 'success' },
    { label: 'Outstanding (Saat Ini)', value: fIDRCompact(s.total_outstanding), accent: 'warn', sub: 'kondisi saat ini' },
    { label: 'Overdue (Saat Ini)', value: fIDRCompact(s.total_overdue), accent: 'danger', sub: 'kondisi saat ini' },
    { label: 'Collection Rate', value: `${(s.collection_rate || 0).toFixed(1)}%`, accent: s.collection_rate >= 80 ? 'success' : s.collection_rate >= 50 ? 'warn' : 'danger' },
  ];
  const trend = data?.charts?.invoice_vs_payment || [];
  const pvo = data?.charts?.paid_vs_outstanding || [];

  return (
    <div className="space-y-4">
      <SectionHeader
        icon={FileText} title="Ringkasan Laporan"
        action={<ExportButtons tab="summary" filters={filters} hasPdf />}
      />
      <KpiGrid items={cards} loading={loading} columns={4} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-card border-border lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Invoice vs Pembayaran</CardTitle></CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-[240px] w-full" /> : trend.length === 0 ? <ReportEmpty>Belum ada data.</ReportEmpty> : (
              <div className="h-[240px] w-full">
                <ResponsiveContainer>
                  <LineChart data={trend} margin={{ left: 0, right: 6, top: 6, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: CHART_AXIS }} />
                    <YAxis tick={{ fontSize: 10, fill: CHART_AXIS }} tickFormatter={(v) => fIDRCompact(v).replace('Rp ', '')} />
                    <Tooltip contentStyle={TT_STYLE} labelStyle={TT_LABEL} itemStyle={TT_ITEM} formatter={(v, n) => [fIDR(v), n === 'invoice' ? 'Invoice' : 'Pembayaran']} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#CBD5E1' }} formatter={(v) => <span style={{ color: '#CBD5E1' }}>{v === 'invoice' ? 'Invoice Diterbitkan' : 'Pembayaran Masuk'}</span>} />
                    <Line type="monotone" dataKey="invoice" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="payment" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Paid vs Outstanding</CardTitle></CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-[240px] w-full" /> : (
              <div className="h-[240px] w-full">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pvo} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="#0B1020" strokeWidth={2}>
                      {pvo.map((e, i) => <Cell key={e.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={TT_STYLE} labelStyle={TT_LABEL} itemStyle={TT_ITEM} formatter={(v, n) => [fIDR(v), n]} />
                    <Legend formatter={(v) => <span style={{ color: '#CBD5E1' }}>{v}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100">Ringkasan Per Bulan</CardTitle></CardHeader>
        <CardContent className="p-0 sm:p-4">
          <ReportTable
            getRowKey={(r) => r.month_key}
            columns={[
              { key: 'month_label', label: 'Bulan' },
              { key: 'invoice_count', label: 'Jml Inv', align: 'right' },
              { key: 'total_dpp', label: 'DPP', align: 'right', render: r => <span className="font-mono-num text-slate-100">{fIDR(r.total_dpp)}</span> },
              { key: 'total_dpp_nilai_lain', label: 'DPP Nilai Lain', align: 'right', render: r => <span className="font-mono-num text-slate-300">{fIDR(r.total_dpp_nilai_lain)}</span> },
              { key: 'total_ppn', label: 'PPN', align: 'right', render: r => <span className="font-mono-num text-primary">{fIDR(r.total_ppn)}</span> },
              { key: 'total_pph', label: 'PPh', align: 'right', render: r => <span className="font-mono-num text-amber-300">{fIDR(r.total_pph)}</span> },
              { key: 'grand_total', label: 'Grand Total', align: 'right', render: r => <span className="font-mono-num text-slate-100">{fIDR(r.grand_total)}</span> },
              { key: 'paid', label: 'Terbayar', align: 'right', render: r => <span className="font-mono-num text-emerald-300">{fIDR(r.paid)}</span> },
              { key: 'outstanding', label: 'Outstanding', align: 'right', render: r => <span className="font-mono-num text-amber-200">{fIDR(r.outstanding)}</span> },
              { key: 'overdue', label: 'Overdue', align: 'right', render: r => <span className="font-mono-num text-red-300">{fIDR(r.overdue)}</span> },
              { key: 'collection_rate', label: 'Coll. %', align: 'right', render: r => <span className="font-mono-num text-slate-100">{r.collection_rate}%</span> },
            ]}
            rows={data?.rows || []}
            emptyText="Belum ada data untuk periode ini."
            mobileCard={(r) => (
              <div className="text-sm space-y-1">
                <div className="font-semibold text-slate-100">{r.month_label} <span className="text-xs text-slate-400">• {r.invoice_count} inv</span></div>
                <div className="grid grid-cols-2 gap-1 text-xs text-slate-300">
                  <div>DPP: <span className="font-mono-num text-slate-100">{fIDR(r.total_dpp)}</span></div>
                  <div>PPN: <span className="font-mono-num text-primary">{fIDR(r.total_ppn)}</span></div>
                  <div>PPh: <span className="font-mono-num text-amber-300">{fIDR(r.total_pph)}</span></div>
                  <div>Grand: <span className="font-mono-num text-slate-100">{fIDR(r.grand_total)}</span></div>
                  <div>Paid: <span className="font-mono-num text-emerald-300">{fIDR(r.paid)}</span></div>
                  <div>Out: <span className="font-mono-num text-amber-200">{fIDR(r.outstanding)}</span></div>
                  <div>Overdue: <span className="font-mono-num text-red-300">{fIDR(r.overdue)}</span></div>
                  <div>Coll: <span className="font-mono-num text-slate-100">{r.collection_rate}%</span></div>
                </div>
              </div>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
